import type { QnaArea } from "@/lib/qnaAreas";
import { BackendError, backendJson } from "@/lib/backend";
import type { DatabookQaQuestion } from "./databook";

export interface QnaQuestion extends DatabookQaQuestion {
  id: string;
  revision: number;
  is_custom: boolean;
  status: "Open" | "Accepted" | "Closed" | "Rejected";
  answer: string | null;
  follow_up: string | null;
  reviewed: boolean;
  deal_implication: string | null;
}

export interface QnaRegister {
  databook_version_id?: string | null;
  questions: QnaQuestion[];
  sheet_names: string[];
  generated_at: string | null;
}

export type QuestionUpdate = Partial<
  Pick<
    QnaQuestion,
    | "question"
    | "rationale"
    | "answer"
    | "status"
    | "priority"
    | "deal_implication"
  >
>;
export interface QuestionCreate {
  databook_tab: string;
  area: QnaArea;
  question: string;
  answer?: string | null;
  period?: string;
  priority?: QnaQuestion["priority"];
}

export interface MatchedAnswer {
  question_id: string;
  question: string;
  answer: string;
  revision: number;
}

async function request<T>(
  projectId: string,
  path: string,
  method = "GET",
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const { response, data } = await backendJson<T>(
    `/api/projects/${encodeURIComponent(projectId)}/fdd-qa${path}`,
    {
      method,
      signal,
      ...(body === undefined
        ? {}
        : {
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }),
    },
  );
  if (response.ok && data !== null) return data;
  throw new BackendError(
    response.status === 409
      ? "This question changed. Reload the Q&A before saving again."
      : "Could not complete the Q&A action. Please try again.",
    { status: response.status },
  );
}

export const getQnaQuestions = (
  projectId: string,
  signal?: AbortSignal,
) =>
  request<QnaRegister>(
    projectId,
    "/questions",
    "GET",
    undefined,
    signal,
  );
export const updateFddQaQuestion = (
  projectId: string,
  question: Pick<QnaQuestion, "id" | "revision">,
  update: QuestionUpdate,
) =>
  request<QnaQuestion>(
    projectId,
    `/questions/${encodeURIComponent(question.id)}`,
    "PATCH",
    { ...update, revision: question.revision },
  );
export const reviewQnaAnswer = (
  projectId: string,
  question: QnaQuestion,
) =>
  request<QnaQuestion>(
    projectId,
    `/questions/${encodeURIComponent(question.id)}/review`,
    "POST",
    { revision: question.revision },
  );
export const matchMeetingNotes = (
  projectId: string,
  notes: string,
) =>
  request<MatchedAnswer[]>(
    projectId,
    "/match-meeting-notes",
    "POST",
    { notes },
  );
export const generateDealImplications = (
  projectId: string,
) =>
  request<QnaQuestion[]>(
    projectId,
    "/deal-implications/generate",
    "POST",
  );

export const createFddQaQuestion = (
  projectId: string,
  question: QuestionCreate,
) => request<QnaQuestion>(projectId, "/questions", "POST", question);
